import React, { useRef, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaFileAlt, FaTrash, FaCheck, FaArrowRight, FaTimes, FaEye } from "react-icons/fa";
import { FiUploadCloud } from "react-icons/fi";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { uploadUserFile } from "../api/staff.api";
import { getStaffId, getStaffName } from "../utils/storage";
import { toast } from "../Components/Toast";

const RECORD_TYPES = [
  "Lab Results",
  "Prescriptions",
  "X-Rays / Scans",
  "Pharmacy",
  "Other",
];

const MAX_BYTES = 10 * 1024 * 1024;

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const inputClass = "w-full border border-surface-container rounded-xl px-3 py-2.5 min-h-[44px] text-sm bg-surface-lowest";

const Stepper = ({ step }) => {
  const steps = [
    { num: 1, label: "Upload" },
    { num: 2, label: "Details" },
    { num: 3, label: "Done" },
  ];

  return (
    <div className="flex items-center justify-center gap-0 mb-6 sm:mb-8">
      {steps.map(({ num, label }, i) => {
        const done = step > num;
        const active = step === num;
        return (
          <React.Fragment key={num}>
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                  done || active
                    ? "bg-primary text-white"
                    : "bg-surface-container text-on-surface-variant"
                }`}
              >
                {done ? <FaCheck size={14} /> : num}
              </div>
              <span
                className={`text-xs mt-1.5 font-medium ${
                  active ? "text-primary font-semibold" : done ? "text-primary" : "text-on-surface-variant"
                }`}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`w-12 sm:w-20 h-0.5 mb-6 ${
                  step > num ? "bg-primary" : "bg-surface-container"
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const FilePreview = ({ file, onRemove }) => {
  const previewUrl = useMemo(() => URL.createObjectURL(file), [file]);
  const canPreview = file.type === "application/pdf" || file.type.startsWith("image/");
  return (
    <div className="flex items-center gap-3 bg-primary-fixed/20 border border-primary-fixed-dim rounded-xl p-3 mb-6">
      <FaFileAlt className="text-primary shrink-0" size={20} />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm break-words">{file.name}</p>
        <p className="text-xs text-on-surface-variant">{formatFileSize(file.size)}</p>
      </div>
      {canPreview && (
        <button
          type="button"
          onClick={() => window.open(previewUrl, "_blank", "noopener")}
          className="text-primary p-2 rounded-full hover:bg-primary-fixed/30"
          aria-label="Preview file"
        >
          <FaEye size={14} />
        </button>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="text-error p-2 rounded-full hover:bg-error-container/30"
        aria-label="Remove file"
      >
        <FaTrash size={14} />
      </button>
    </div>
  );
};

const UploadMedicalRecordPage = () => {
  const navigate = useNavigate();
  const staffId = getStaffId();
  const staffName = getStaffName() || "";
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [recordType, setRecordType] = useState("");
  const [dateOfRecord, setDateOfRecord] = useState("");
  const [patientName, setPatientName] = useState(staffName);
  const [notes, setNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const pickFile = (selected) => {
    if (!selected) return;
    if (selected.size > MAX_BYTES) {
      setError("File must be 10MB or smaller.");
      return;
    }
    setError("");
    setFile(selected);
    if (selected.type === "application/pdf" || selected.type.startsWith("image/")) {
      window.open(URL.createObjectURL(selected), "_blank", "noopener");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    pickFile(e.dataTransfer.files[0]);
  };

  const handleSave = async () => {
    if (!file || !recordType || !dateOfRecord || !patientName.trim()) {
      setError("Please fill all required fields.");
      return;
    }
    if (!staffId) {
      setError("You are not logged in.");
      return;
    }

    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("recordType", recordType);
      formData.append("dateOfRecord", dateOfRecord);
      formData.append("patientName", patientName.trim());
      if (notes.trim()) formData.append("notes", notes.trim());

      await uploadUserFile(staffId, formData);
      toast.success("Medical record uploaded successfully.");
      setStep(3);
    } catch {
      setError("Upload failed. Please try again.");
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const showStickyBar = step === 2 && file;

  return (
    <div className="bg-surface-low min-h-screen flex flex-col">
      <Header />
      <div className={`flex flex-col flex-1 ${showStickyBar ? "pb-28 lg:pb-0" : "pb-16 lg:pb-0"}`}>
        <div className="flex-1 max-w-lg mx-auto w-full px-3 sm:px-4 py-4 sm:py-8">
          <div className="bg-surface-lowest rounded-2xl shadow-sm border border-surface-container w-full p-4 sm:p-6 relative">
            <button
              type="button"
              onClick={() => navigate("/medicalrecord")}
              className="absolute top-3 right-3 p-2 rounded-full text-on-surface-variant hover:text-on-primary-fixed-var hover:bg-surface-low"
              aria-label="Close"
            >
              <FaTimes size={18} />
            </button>

            <h1 className="text-xl font-bold text-on-primary-fixed-var text-center mb-1 pr-8">Upload Document</h1>
            <p className="text-sm text-on-surface-variant text-center mb-6 leading-relaxed">
              Add a new medical record to your profile.
            </p>

            <Stepper step={step} />

            {error && (
              <p className="text-error text-sm mb-4 text-center bg-error-container/30 border border-error/20 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            {step === 1 && (
              <>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-surface-container rounded-xl min-h-[180px] flex flex-col items-center justify-center p-6 text-center cursor-pointer hover:border-primary/40 hover:bg-primary-fixed/10 transition-colors mb-6"
                >
                  <FiUploadCloud className="text-primary mb-3" size={40} />
                  <p className="font-semibold text-on-primary-fixed-var mb-1">
                    Drag & drop or click to upload
                  </p>
                  <p className="text-xs text-on-surface-variant leading-relaxed">PDF, JPG, PNG — max 10MB</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,image/jpeg,image/png,image/jpg"
                    className="hidden"
                    onChange={(e) => pickFile(e.target.files[0])}
                  />
                </div>

                {file && <FilePreview file={file} onRemove={() => setFile(null)} />}

                <button
                  type="button"
                  disabled={!file}
                  onClick={() => { setError(""); setStep(2); }}
                  className="w-full min-h-[48px] py-3 rounded-xl bg-primary text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-container"
                >
                  Continue →
                </button>
              </>
            )}

            {step === 2 && file && (
              <>
                <FilePreview file={file} onRemove={() => { setFile(null); setStep(1); }} />

                <div className="space-y-4 mb-6 lg:mb-0">
                  <div>
                    <label className="block text-sm font-medium text-on-primary-fixed-var mb-1">
                      Record Type
                    </label>
                    <select
                      value={recordType}
                      onChange={(e) => setRecordType(e.target.value)}
                      className={inputClass}
                    >
                      <option value="">Select a category</option>
                      {RECORD_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-primary-fixed-var mb-1">
                      Date of Record
                    </label>
                    <input
                      type="date"
                      value={dateOfRecord}
                      onChange={(e) => setDateOfRecord(e.target.value)}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-primary-fixed-var mb-1">
                      Patient Name
                    </label>
                    <input
                      type="text"
                      value={patientName}
                      onChange={(e) => setPatientName(e.target.value)}
                      placeholder="E.g., Jane Doe"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-on-primary-fixed-var mb-1">
                      Additional Notes (Optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any relevant context..."
                      rows={3}
                      className={`${inputClass} min-h-[88px] resize-none py-3`}
                    />
                  </div>
                </div>

                <div className="hidden lg:flex gap-3 pt-4 border-t border-surface-container">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 min-h-[48px] py-3 rounded-xl border-2 border-primary text-primary font-semibold hover:bg-primary-fixed/20"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={uploading}
                    className="flex-1 min-h-[48px] py-3 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 hover:bg-primary-container disabled:opacity-50"
                  >
                    {uploading ? "Saving..." : (
                      <>
                        Save Document
                        <FaArrowRight size={14} />
                      </>
                    )}
                  </button>
                </div>
              </>
            )}

            {step === 3 && (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-primary mx-auto flex items-center justify-center mb-4">
                  <FaCheck className="text-white" size={24} />
                </div>
                <h2 className="text-lg font-bold text-on-primary-fixed-var mb-2">Document Saved</h2>
                <p className="text-sm text-on-surface-variant mb-6 leading-relaxed">
                  Your medical record has been uploaded to your vault.
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/medicalrecord")}
                  className="w-full min-h-[48px] py-3 rounded-xl bg-primary text-white font-semibold hover:bg-primary-container"
                >
                  Back to Medical Records
                </button>
              </div>
            )}
          </div>
        </div>

        {showStickyBar && (
          <div className="lg:hidden fixed bottom-16 inset-x-0 z-30 bg-surface-lowest border-t border-surface-container px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
            <div className="flex gap-3 max-w-lg mx-auto">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 min-h-[48px] py-3 rounded-xl border-2 border-primary text-primary font-semibold"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={uploading}
                className="flex-1 min-h-[48px] py-3 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {uploading ? "Saving..." : (
                  <>
                    Save
                    <FaArrowRight size={14} />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        <Footer />
      </div>
    </div>
  );
};

export default UploadMedicalRecordPage;
